import { supabase } from './supabase.js';

/**
 * AI Intelligence Feed Crawler
 * 
 * IMPORTANT: This crawler runs in the browser and uses enhanced fallback scoring.
 * 
 * For AI-powered relevance scoring with Claude/GPT:
 * 1. Create a backend API endpoint (e.g., /api/score-content)
 * 2. Move Claude API calls to the backend to avoid CORS issues
 * 3. Call your backend from this crawler instead of third-party APIs directly
 * 
 * Current features:
 * - RSS feed parsing with CORS proxy
 * - ArXiv paper fetching
 * - Hacker News AI story filtering
 * - Enhanced keyword-based relevance scoring
 * - Duplicate handling with URL-based upserts
 */
class RealFeedCrawler {
  constructor() {
    this.isRunning = false;
    this.crawlInterval = null;
    this.claudeApiKey = import.meta.env.VITE_CLAUDE_API_KEY;
  }

  // RSS Parser using DOMParser (works in browser)
  parseRSS(xmlText) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, 'text/xml');
    
    if (doc.querySelector('parsererror')) {
      throw new Error('Failed to parse RSS XML');
    }

    const items = [];
    const entries = doc.querySelectorAll('item, entry');
    
    entries.forEach(entry => {
      const title = entry.querySelector('title')?.textContent?.trim();
      const link = entry.querySelector('link')?.textContent?.trim() || 
                  entry.querySelector('link')?.getAttribute('href');
      const description = entry.querySelector('description, summary')?.textContent?.trim();
      const pubDate = entry.querySelector('pubDate, published, updated')?.textContent?.trim();
      
      if (title && link) {
        items.push({
          title,
          url: link,
          summary: this.cleanHtml(description),
          published_at: this.parseDate(pubDate)
        });
      }
    });

    return items;
  }

  // Clean HTML from descriptions
  cleanHtml(html) {
    if (!html) return '';
    
    // Create a temporary element to extract text content
    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    // Remove script and style elements
    const scripts = temp.querySelectorAll('script, style');
    scripts.forEach(el => el.remove());
    
    // Get text content and clean up whitespace
    return temp.textContent.replace(/\s+/g, ' ').trim().substring(0, 500);
  }

  // Parse various date formats
  parseDate(dateString) {
    if (!dateString) return new Date().toISOString();
    
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
    } catch {
      return new Date().toISOString();
    }
  }

  // Fetch RSS feed using CORS proxy
  async fetchRSSFeed(url) {
    try {
      // Use a CORS proxy for RSS feeds (or your own backend endpoint)
      const proxyUrl = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(url)}`;
      
      const response = await fetch(proxyUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.status !== 'ok') {
        throw new Error(`RSS2JSON error: ${data.message}`);
      }

      // Convert RSS2JSON format to our format
      return data.items.map(item => ({
        title: item.title,
        url: item.link,
        summary: this.cleanHtml(item.description),
        published_at: this.parseDate(item.pubDate),
        content: this.cleanHtml(item.content)
      }));
    } catch (error) {
      console.error(`Error fetching RSS feed ${url}:`, error);
      return [];
    }
  }

  // Fetch from ArXiv API
  async fetchArXivPapers(category = 'cs.AI', maxResults = 10) {
    try {
      const url = `https://export.arxiv.org/api/query?search_query=cat:${category}&start=0&max_results=${maxResults}&sortBy=submittedDate&sortOrder=descending`;
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`ArXiv API error! status: ${response.status}`);
      }
      
      const xmlText = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlText, 'text/xml');
      
      const entries = doc.querySelectorAll('entry');
      const papers = [];
      
      entries.forEach(entry => {
        const title = entry.querySelector('title')?.textContent?.trim();
        const id = entry.querySelector('id')?.textContent?.trim();
        const summary = entry.querySelector('summary')?.textContent?.trim();
        const published = entry.querySelector('published')?.textContent?.trim();
        
        if (title && id) {
          papers.push({
            title: title.replace(/\s+/g, ' '),
            url: id,
            summary: summary?.replace(/\s+/g, ' ').substring(0, 500),
            published_at: this.parseDate(published),
            content: summary?.replace(/\s+/g, ' ')
          });
        }
      });
      
      return papers;
    } catch (error) {
      console.error('Error fetching ArXiv papers:', error);
      return [];
    }
  }

  // Fetch from Hacker News API for AI-related stories
  async fetchHackerNewsAI() {
    try {
      // Get top stories
      const topStoriesResponse = await fetch('https://hacker-news.firebaseio.com/v0/topstories.json');
      const topStoryIds = await topStoriesResponse.json();
      
      // Get first 50 stories and filter for AI-related content
      const storyPromises = topStoryIds.slice(0, 50).map(id =>
        fetch(`https://hacker-news.firebaseio.com/v0/item/${id}.json`).then(r => r.json())
      );
      
      const stories = await Promise.all(storyPromises);
      
      // Filter for AI-related content
      const aiKeywords = ['ai', 'artificial intelligence', 'machine learning', 'neural', 'gpt', 'claude', 'llm', 'openai', 'anthropic', 'google ai', 'deepmind'];
      
      const aiStories = stories.filter(story => {
        if (!story || !story.title) return false;
        const titleLower = story.title.toLowerCase();
        return aiKeywords.some(keyword => titleLower.includes(keyword));
      });

      return aiStories.map(story => ({
        title: story.title,
        url: story.url || `https://news.ycombinator.com/item?id=${story.id}`,
        summary: story.text ? this.cleanHtml(story.text).substring(0, 500) : '',
        published_at: new Date(story.time * 1000).toISOString(),
        content: story.text ? this.cleanHtml(story.text) : ''
      }));
    } catch (error) {
      console.error('Error fetching Hacker News:', error);
      return [];
    }
  }

  // Calculate relevance score using enhanced fallback algorithm
  // Note: Direct Claude API calls from browser are blocked by CORS
  // For AI-powered scoring, implement a backend service
  async calculateRelevanceScore(item) {
    // Skip Claude API calls from browser - use enhanced fallback scoring
    console.log('🧠 Using enhanced fallback scoring (Claude API requires backend service)');
    return this.fallbackRelevanceScore(item);
  }

  // Enhanced fallback relevance scoring algorithm
  fallbackRelevanceScore(item) {
    const title = item.title?.toLowerCase() || '';
    const summary = item.summary?.toLowerCase() || '';
    const text = title + ' ' + summary;
    
    let score = 25; // Base score
    
    // Tier 1: High-impact AI developments (40 points max)
    const tier1Keywords = [
      'gpt-4', 'gpt-5', 'claude-3', 'claude-4', 'gemini', 'o1', 'strawberry',
      'model release', 'breakthrough', 'agi', 'multimodal', 'reasoning'
    ];
    let tier1Matches = 0;
    tier1Keywords.forEach(keyword => {
      if (text.includes(keyword)) { score += 20; tier1Matches++; }
    });
    
    // Tier 2: Industry/API developments (30 points max)
    const tier2Keywords = [
      'api', 'endpoint', 'sdk', 'integration', 'pricing', 'enterprise',
      'function calling', 'tool use', 'fine-tuning', 'custom model'
    ];
    let tier2Matches = 0;
    tier2Keywords.forEach(keyword => {
      if (text.includes(keyword)) { score += 15; tier2Matches++; }
    });
    
    // Tier 3: Core AI terms (20 points max)
    const tier3Keywords = [
      'openai', 'anthropic', 'google ai', 'microsoft ai', 'meta ai',
      'transformer', 'llm', 'large language model', 'neural network'
    ];
    let tier3Matches = 0;
    tier3Keywords.forEach(keyword => {
      if (text.includes(keyword)) { score += 8; tier3Matches++; }
    });
    
    // Tier 4: General AI terms (10 points max)
    const tier4Keywords = [
      'artificial intelligence', 'machine learning', 'deep learning',
      'ai', 'ml', 'nlp', 'computer vision'
    ];
    tier4Keywords.forEach(keyword => {
      if (text.includes(keyword)) score += 3;
    });
    
    // Content type bonuses
    if (text.includes('paper') || text.includes('arxiv') || text.includes('research')) {
      score += 12; // Academic research bonus
    }
    
    if (text.includes('release') || text.includes('launch') || text.includes('announce')) {
      score += 15; // Product announcement bonus
    }
    
    if (text.includes('beta') || text.includes('preview') || text.includes('early access')) {
      score += 18; // Early access bonus
    }
    
    // Quality indicators
    if (text.includes('performance') || text.includes('benchmark') || text.includes('evaluation')) {
      score += 10;
    }
    
    if (text.includes('open source') || text.includes('github') || text.includes('code')) {
      score += 8;
    }
    
    // Penalty for overly generic content
    if (tier1Matches === 0 && tier2Matches === 0 && tier3Matches < 2) {
      score -= 10;
    }
    
    // Title relevance bonus (titles are more curated)
    const titleOnlyText = title;
    if (tier1Keywords.some(k => titleOnlyText.includes(k))) score += 10;
    if (tier2Keywords.some(k => titleOnlyText.includes(k))) score += 5;
    
    return Math.min(100, Math.max(0, score));
  }

  // Calculate trending score based on recency and keywords
  calculateTrendingScore(item) {
    const now = new Date();
    const publishedDate = new Date(item.published_at);
    const hoursAgo = (now - publishedDate) / (1000 * 60 * 60);
    
    let score = 0;
    
    // Recency bonus (higher for newer items)
    if (hoursAgo < 1) score += 50;
    else if (hoursAgo < 6) score += 30;
    else if (hoursAgo < 24) score += 20;
    else if (hoursAgo < 72) score += 10;
    
    // Trending keywords
    const title = item.title?.toLowerCase() || '';
    const summary = item.summary?.toLowerCase() || '';
    const text = title + ' ' + summary;
    
    const trendingKeywords = ['breaking', 'just announced', 'preview', 'beta', 'launch', 'release'];
    trendingKeywords.forEach(keyword => {
      if (text.includes(keyword)) score += 20;
    });
    
    return Math.min(100, Math.max(0, score));
  }

  // Extract tags from content
  extractTags(item) {
    const title = item.title?.toLowerCase() || '';
    const summary = item.summary?.toLowerCase() || '';
    const text = title + ' ' + summary;
    
    const tags = [];
    
    // Model releases
    if (text.includes('gpt') || text.includes('claude') || text.includes('gemini')) {
      tags.push('model_release');
    }
    
    // API changes
    if (text.includes('api') || text.includes('endpoint')) {
      tags.push('api_changes');
    }
    
    // Performance
    if (text.includes('performance') || text.includes('speed') || text.includes('optimization')) {
      tags.push('performance');
    }
    
    // Beta access
    if (text.includes('beta') || text.includes('preview') || text.includes('early access')) {
      tags.push('beta_access');
    }
    
    // Research
    if (text.includes('paper') || text.includes('research') || text.includes('study')) {
      tags.push('research');
    }
    
    // Enterprise
    if (text.includes('enterprise') || text.includes('business') || text.includes('team')) {
      tags.push('enterprise');
    }
    
    return tags;
  }

  // Process a single source
  async processSource(source) {
    console.log(`🔄 Processing source: ${source.name} (ID: ${source.id})`);
    let items = [];
    
    // Validate source exists
    if (!source.id) {
      console.error(`❌ Source ${source.name} has no ID`);
      return 0;
    }
    
    try {
      switch (source.source_type) {
        case 'company_blog':
        case 'news_site':
          items = await this.fetchRSSFeed(source.url);
          break;
        case 'paper_archive':
          if (source.company === 'arxiv') {
            items = await this.fetchArXivPapers();
          }
          break;
        case 'hacker_news':
          items = await this.fetchHackerNewsAI();
          break;
        default:
          items = await this.fetchRSSFeed(source.url);
      }

      if (items.length === 0) {
        console.log(`⚠️ No items found for ${source.name}`);
        return 0;
      }

      // Process each item
      const processedItems = [];
      for (const item of items) {
        if (!item.title || !item.url) {
          console.log(`⚠️ Skipping invalid item: title=${!!item.title}, url=${!!item.url}`);
          continue; // Skip invalid items
        }

        try {
          const relevanceScore = await this.calculateRelevanceScore(item);
          const trendingScore = this.calculateTrendingScore(item);
          const tags = this.extractTags(item);
          
          // Validate data before adding
          const processedItem = {
            source_id: source.id,
            title: item.title.substring(0, 500), // Ensure title fits in DB
            url: item.url.substring(0, 500), // Ensure URL fits in DB
            summary: item.summary?.substring(0, 1000) || '', // Ensure summary fits
            content: item.content?.substring(0, 5000) || '', // Truncate content
            published_at: item.published_at || new Date().toISOString(), // Ensure valid timestamp
            relevance_score: parseFloat(relevanceScore.toFixed(2)), // Ensure decimal format
            trending_score: parseFloat(trendingScore.toFixed(2)), // Ensure decimal format
            tags: tags, // Already an array
            metadata: {
              company: source.company,
              sourceType: source.source_type,
              processedAt: new Date().toISOString()
            }
          };
          
          // Final validation
          if (!processedItem.source_id || !processedItem.title || !processedItem.url) {
            console.log(`⚠️ Skipping item with missing required fields:`, {
              source_id: !!processedItem.source_id,
              title: !!processedItem.title,
              url: !!processedItem.url
            });
            continue;
          }
          
          processedItems.push(processedItem);
        } catch (itemError) {
          console.error(`Error processing item "${item.title}":`, itemError);
          continue; // Skip problematic items
        }
      }

      // Insert into database using upsert to handle duplicates
      if (processedItems.length > 0) {
        const { data, error } = await supabase
          .from('ai_feed_items')
          .upsert(processedItems, { 
            onConflict: 'url'
          })
          .select();

        if (error) {
          console.error(`❌ Database error for ${source.name}:`, error);
          console.error('Sample data being inserted:', processedItems[0]);
          console.error(`Trying to insert ${processedItems.length} items`);
          // Don't throw, just log and continue
          return 0;
        } else {
          const insertedCount = data ? data.length : 0;
          console.log(`✅ Successfully processed ${insertedCount} items from ${source.name} (${processedItems.length - insertedCount} were duplicates)`);
          
          // Update source last_crawled timestamp
          await supabase
            .from('ai_feed_sources')
            .update({ last_crawled: new Date().toISOString() })
            .eq('id', source.id);

          return insertedCount;
        }
      } else {
        console.log(`⚠️ No valid items to insert for ${source.name}`);
        return 0;
      }
    } catch (error) {
      console.error(`❌ Error processing source ${source.name}:`, error.message);
      // Don't throw - let the crawl cycle continue with other sources
      return 0;
    }
  }

  // Run full crawl cycle
  async runCrawlCycle() {
    if (this.isRunning) {
      console.log('Crawl cycle already running, skipping...');
      return {
        success: false,
        error: 'Crawl already running',
        timestamp: new Date().toISOString()
      };
    }

    this.isRunning = true;
    console.log('🚀 Starting AI intelligence crawl cycle...');
    
    try {
      // Get all active sources
      const { data: sources, error } = await supabase
        .from('ai_feed_sources')
        .select('*')
        .eq('active', true);

      if (error) {
        throw error;
      }

      let totalItemsProcessed = 0;
      let successfulSources = 0;
      let failedSources = 0;

      // Process each source
      for (const source of sources) {
        try {
          const itemsProcessed = await this.processSource(source);
          totalItemsProcessed += itemsProcessed;
          successfulSources++;
          console.log(`✅ ${source.name}: ${itemsProcessed} items`);
        } catch (error) {
          failedSources++;
          console.error(`❌ ${source.name}: ${error.message}`);
        }
        
        // Add delay between sources to be respectful
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      console.log(`✅ Crawl cycle complete. Processed ${totalItemsProcessed} total items from ${successfulSources}/${sources.length} sources.`);
      
      return {
        success: true,
        sourcesProcessed: sources.length,
        successfulSources,
        failedSources,
        itemsProcessed: totalItemsProcessed,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('❌ Error in crawl cycle:', error);
      return {
        success: false,
        error: error.message,
        timestamp: new Date().toISOString()
      };
    } finally {
      this.isRunning = false;
    }
  }

  // Start automated crawling
  startAutoCrawling(intervalHours = 6) {
    if (this.crawlInterval) {
      console.log('Auto-crawling already started');
      return;
    }

    console.log(`Starting auto-crawling every ${intervalHours} hours`);
    
    // Run initial crawl
    this.runCrawlCycle();
    
    // Set up interval
    this.crawlInterval = setInterval(() => {
      this.runCrawlCycle();
    }, intervalHours * 60 * 60 * 1000);
  }

  // Stop automated crawling
  stopAutoCrawling() {
    if (this.crawlInterval) {
      clearInterval(this.crawlInterval);
      this.crawlInterval = null;
      console.log('Stopped auto-crawling');
    }
  }

  // Manual trigger for immediate crawl
  async triggerManualCrawl() {
    return await this.runCrawlCycle();
  }

  // Get crawler status
  getStatus() {
    return {
      isRunning: this.isRunning,
      hasApiKey: !!this.claudeApiKey,
      autoInterval: !!this.crawlInterval,
      lastRun: new Date().toISOString()
    };
  }
}

export const realFeedCrawler = new RealFeedCrawler();